import {
    AccomodationSelected,
    Prestation,
    OptionSelected,
    GlobalSelected,
    ParticipantSelected,
    Booking,
} from "../classes"
import BookingData from "@/classes/BookingData"
import bookingData from "@/computed/bookingData"
import { IFee } from "@/model/Fee/Fee"
import { ITraveller } from "@/model/Traveller"
import { IAccomodation } from "@/model/Accomodation"
import { IService } from "@/model/Service/Service"
import { IInsurance } from "@/model/Insurance/Insurance"
import GlobalInsurance, { IGlobalInsurance } from "@/model/Insurance/GlobalInsurance"
import { BookingStatusEnum, QuoteResponseDTO, QuoteState } from "../../../../requests-module"


const getPrestations = (bookingDataObj: BookingData): Prestation[] => {
    const arr: Prestation[] = []

    const accomodationsArr: IAccomodation[] = bookingDataObj.accomodationsArr
    accomodationsArr.forEach((accomodation: IAccomodation) => {
        const prestIdSource: string = accomodation.getIdFull() // FIXME should I use the full id? Do I even have a fusion id?
        const amount: number | undefined = accomodation.getQuantity() // actually amount is quantity in tracking, and not the price
        if (amount != 0 && !amount) throw new Error("amount cannot be undefined")
        const prestation: Prestation = new Prestation(
            prestIdSource,
            amount,
        )
        arr.push(prestation)
    })

    return arr
}

const getGlobalSelected = (bookingDataObj: BookingData): { [id: string]: GlobalSelected } => {
    const obj: { [id: string]: GlobalSelected } = {}

    const insurancesArr: IInsurance[] = bookingDataObj.insurancesArr
    const globalInsurance: IGlobalInsurance | undefined = insurancesArr.find((insurance: IInsurance) => {
        return insurance instanceof GlobalInsurance
    }) as IGlobalInsurance | undefined
    if (globalInsurance) {
        const id = globalInsurance.getIdFull()
        const price: number | null = globalInsurance.getTotalPrice()
        if (price != 0 && !price) throw new Error("price cannot be undefined")
        const description: string = globalInsurance.getInsuranceContent()?.getDescription() || ""
        const globalSelected: GlobalSelected = new GlobalSelected(
            id,
            price,
            description,
        )
        obj[id] = globalSelected
    }

    return obj
}

const getParticipantOptions = (traveller: ITraveller): OptionSelected[] => {
    const arr: OptionSelected[] = []

    const selectedServices: IService[] = traveller.getServicesSelected()
    selectedServices.forEach((selectedService: IService) => {
        const id: string = selectedService.getIdFull()
        const description: string = selectedService.getServiceContent()?.getDescription() || ""
        const price: number | undefined = selectedService.getBaseServiceQuoteObject()?.totalPrice
        if (price != 0 && !price) throw new Error("price cannot be undefined")
        const quantity: number | undefined = selectedService.getQuantity()
        if (quantity != 0 && !quantity) throw new Error("quantity cannot be undefined")
        const optionSelected: OptionSelected = new OptionSelected(
            id,
            description,
            price,
            quantity,
        )
        arr.push(optionSelected)
    })

    return arr
}

const getParticipantsSelected = (bookingDataObj: BookingData): ParticipantSelected[] => {
    const arr: ParticipantSelected[] = []

    bookingDataObj.travellersArr.forEach((traveller) => {
        const options: OptionSelected[] = getParticipantOptions(traveller)
        const participantSelected: ParticipantSelected = new ParticipantSelected(options)
        arr.push(participantSelected)
    })

    return arr
}
const getAccomodationsSelected = (bookingDataObj: BookingData): AccomodationSelected[] => {
    const arr: AccomodationSelected[] = []

    bookingDataObj.accomodationsArr.forEach((accomodation: IAccomodation) => {
        const id: string = accomodation.getIdFull()
        const description: string | undefined = accomodation.getAccomodationContent()?.getDescription() || ""
        // if (!description) throw new Error("description cannot be undefined")
        const price: number | null = accomodation.getTotalPrice() || NaN
        // if (price != 0 && !price) throw new Error("price cannot be undefined") 
        const quantity: number | undefined = accomodation.getQuantity()
        if (quantity != 0 && !quantity) throw new Error("quantity cannot be undefined")
        const accomodationSelected: AccomodationSelected = new AccomodationSelected(
            id,
            description,
            price,
            quantity,
        )
        arr.push(accomodationSelected)
    })

    return arr
}


const getTotalFeePrice = (bookingDataObj: BookingData): number => {
    let sum = 0
    bookingDataObj.feesArr.forEach((fee: IFee) => {
        sum += fee.getValue()
    })
    return sum
}

const getContractStatusFromBookingStatus = (status: BookingStatusEnum): "Panier en cours" | "Quote" | "Reservation" | undefined => {
    const map: Map<BookingStatusEnum, "Panier en cours" | "Quote" | "Reservation"> = new Map([
        [BookingStatusEnum.PANIER_EN_COURS, "Panier en cours"],
        [BookingStatusEnum.QUOTE, "Quote"],
        [BookingStatusEnum.RESERVATION, "Reservation"],
    ])
    const contractStatus: "Panier en cours" | "Quote" | "Reservation" | undefined = map.get(status)
    return contractStatus
}

const getBookingObject = (): Booking => {

    const bookingQuoteResponse: QuoteResponseDTO | null = bookingData.value.responses.bookingQuoteResponse
    if (!bookingQuoteResponse) throw new Error("bookingQuoteResponse cannot be undefined")
    const booking: QuoteState | null | undefined = bookingQuoteResponse.booking
    if (!booking) throw new Error("booking cannot be undefined")


    const liheId: number | undefined = bookingData.value.lihe.getContent()?.getLiheId()
    const totalPrice: number = bookingData.value.totalPrice
    const totalFeePrice: number = getTotalFeePrice(bookingData.value)

    const prestations: Prestation[] = getPrestations(bookingData.value)

    const contractId: string | undefined = booking.bookingRef
    if (!contractId) throw new Error("contractId cannot be undefined")
    const prices: {
        totalPrice: number;
        totalFeePrice: number;
    } = {
        totalPrice,
        totalFeePrice,
    }
    const bookingStatus: BookingStatusEnum | null | undefined = booking.status
    if (!bookingStatus) throw new Error("bookingStatus cannot be undefined")
    const contractStatus: "Panier en cours" | "Quote" | "Reservation" | undefined = getContractStatusFromBookingStatus(bookingStatus)
    if (!contractStatus) throw new Error("contractStatus cannot be undefined")
    const promo: string = booking.promocode || ""
    const data: {
        prestations: Prestation[]
        liheId: string
    } = {
        prestations,
        liheId: `${liheId}`,
    }
    const packageType = "" // TODO
    const participantsSelected: ParticipantSelected[] = getParticipantsSelected(bookingData.value)
    const globalSelected: { [id: string]: GlobalSelected } = getGlobalSelected(bookingData.value)
    const accomodationsSelected: AccomodationSelected[] = getAccomodationsSelected(bookingData.value)

    return new Booking(
        contractId,
        prices,
        contractStatus,
        promo,
        data,
        packageType,
        participantsSelected,
        globalSelected,
        accomodationsSelected,
    )
}

export default getBookingObject